const WALKUP_KEY = "walkupContext";

export const setWalkUpContext = (eventId, token) => {
  try {
    localStorage.setItem(WALKUP_KEY, JSON.stringify({ eventId, token }));
  } catch (e) {
    console.error("Unable to persist walk-up context", e?.message);
  }
};

export const getWalkUpContext = () => {
  try {
    const raw = localStorage.getItem(WALKUP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const clearWalkUpContext = () => {
  try {
    localStorage.removeItem(WALKUP_KEY);
  } catch (e) {
    /* no-op */
  }
};

// Build Cognito ClientMetadata for the magic-link confirmation email. Always
// includes confirmRedirect so the link works for every signup (system-wide);
// adds walk-up routing context when a scan is in progress. Cognito requires all
// values to be strings.
export const buildConfirmMetadata = () => {
  const metadata = { confirmRedirect: `${window.location.origin}/confirm` };
  const ctx = getWalkUpContext();
  if (ctx?.eventId && ctx?.token) {
    metadata.context = "walkup";
    metadata.eventId = String(ctx.eventId);
    metadata.token = String(ctx.token);
    metadata.next = `/walkup/${ctx.eventId}/${ctx.token}`;
  }
  return metadata;
};

// Flow flag: set when an unauthenticated user lands on a valid walk-up welcome,
// so that once they finish signup/login (becoming signed in) we auto-register
// and skip straight to the app, rather than showing the "Welcome back" screen
// meant for users who arrived already signed in.
const FLOW_KEY = "walkupFlowStarted";

export const setWalkUpFlowStarted = (eventId) => {
  try {
    localStorage.setItem(FLOW_KEY, String(eventId));
  } catch (e) {
    /* no-op */
  }
};

export const getWalkUpFlowStarted = () => {
  try {
    return localStorage.getItem(FLOW_KEY);
  } catch (e) {
    return null;
  }
};

export const clearWalkUpFlowStarted = () => {
  try {
    localStorage.removeItem(FLOW_KEY);
  } catch (e) {
    /* no-op */
  }
};

// Durable "completed walk-up onboarding" flag. Set once a user has scanned,
// gotten an account, and signed in through the walk-up flow. Survives logout
// (logout only clears Cognito keys), so returning scanners skip the welcome /
// create-account default and the walk-in tour.
const ONBOARDED_KEY = "walkupOnboarded";

export const setWalkupOnboarded = () => {
  try {
    localStorage.setItem(ONBOARDED_KEY, "true");
  } catch (e) {
    /* no-op */
  }
};

export const isWalkupOnboarded = () => {
  try {
    return localStorage.getItem(ONBOARDED_KEY) === "true";
  } catch (e) {
    return false;
  }
};

// One-shot "show the walk-in tour" flag. Set at registration for first-time
// walk-ups (decoupled from `walkupOnboarded`, which is set at the same time but
// gates the welcome/create-account default). Cleared when the check-in tour
// finishes, or on any returning scan, so it never lingers into a later visit.
const TOUR_KEY = "walkupTourPending";

export const setWalkupTourPending = () => {
  try {
    localStorage.setItem(TOUR_KEY, "true");
  } catch (e) {
    /* no-op */
  }
};

export const getWalkupTourPending = () => {
  try {
    return localStorage.getItem(TOUR_KEY) === "true";
  } catch (e) {
    return false;
  }
};

export const clearWalkupTourPending = () => {
  try {
    localStorage.removeItem(TOUR_KEY);
  } catch (e) {
    /* no-op */
  }
};

// Parse a `next` path like /walkup/<eventId>/<token> back into context. Used in
// the cross-context magic-link case where localStorage may be empty.
export const contextFromNext = (next) => {
  if (!next) return null;
  const match = next.match(/^\/walkup\/([^/]+)\/([^/?#]+)/);
  if (!match) return null;
  return { eventId: match[1], token: match[2] };
};
